/**
 * @fileoverview Global upscale state store.
 *
 * Tracks active upscale jobs so they can be accessed from multiple UI locations
 * (notification indicator, canvas panels, progress toasts, etc.)
 *
 * Design principles:
 * - Follows exportStore.ts pattern for consistency
 * - Jobs are keyed by upscaleId (from backend)
 * - Supports both image and video upscaling
 * - Tracks version info for display purposes
 */

import { create } from 'zustand'
import type { EnhanceOperation } from '@/types'

// =============================================================================
// Types
// =============================================================================

export type UpscaleStatus = 'pending' | 'queued' | 'processing' | 'uploading' | 'completed' | 'failed'

export interface UpscaleJob {
  /** Unique job ID from backend */
  upscaleId: string
  /** Project this upscale belongs to */
  projectId: string
  /** Scene containing the source asset (null for project-level assets) */
  sceneId?: string | null
  /** Source asset being upscaled */
  sourceAssetId: string
  /** Display name for the source (e.g., "Take 1" or prompt snippet) */
  sourceAssetName: string
  /** Whether this is a video or image upscale */
  assetType: 'video' | 'image'
  /** Current job status */
  status: UpscaleStatus
  /** Progress percentage (0-100) */
  progress: number
  /** Human-readable status message */
  message?: string
  /** Topaz model used (e.g., 'prob-4', 'Standard V2') */
  model: string
  /** Which operation this job runs: upscale / restore (Enhance) / sdr-to-hdr (HDR). */
  operation?: EnhanceOperation
  /** Scale factor (2, 4, or 8) */
  scale: number
  /** Output format (e.g., 'mp4', 'jpeg') */
  outputFormat?: string
  /** Short display label for the chosen HDR output, e.g. 'ProRes' / 'H.265' / 'EXR'. */
  outputLabel?: string
  /** Full user-selected settings for retrying this job after failure. */
  retryOptions?: Record<string, unknown>
  /** ID of the created upscaled asset (set on completion) */
  resultAssetId?: string
  /** Error info if failed */
  error?: {
    code: string
    message: string
  }
  /** Olives refunded on failure */
  refundedAmount?: number
  /** User who started the upscale, used to scope the initiator-only notification card. */
  userId?: string
  /** When the job was created */
  createdAt: Date
  /** When the job completed (success or failure) */
  completedAt?: Date
  /** Last time this job received a progress update (used for staleness detection) */
  lastUpdateAt: Date
  /**
   * True once a real WebSocket progress update has been received. Drives the determinate vs
   * indeterminate progress treatment: a job hydrated from Yjs (collaborator / post-refresh) starts
   * `false` and renders an indeterminate bar until the first live tick arrives, so the bar never
   * paints a fabricated percentage or jumps backward.
   */
  hasLiveProgress?: boolean
}

/**
 * Coarse, durable upscale-job record stored in the collaborative Yjs `upscaleJobs` map.
 *
 * Holds only server-confirmable identity + status — NO live `progress`/`message`/`lastUpdateAt`
 * (those stay ephemeral in {@link UpscaleJob}, fed by WebSocket). Timestamps are ISO strings so the
 * record round-trips through Yjs/IndexedDB as plain JSON identically across clients.
 */
export interface YjsUpscaleJob {
  /** Backend job id (key in the `upscaleJobs` map). */
  upscaleId: string
  projectId: string
  sceneId?: string | null
  sourceAssetId: string
  /** Version group the resulting upscale belongs to (`sourceAsset.versionGroupId ?? sourceAsset.id`). */
  versionGroupId?: string
  sourceAssetName: string
  assetType: 'video' | 'image'
  /** Coarse, server-confirmable status. `'pending'` is client-only and never written to Yjs. */
  status: Exclude<UpscaleStatus, 'pending'>
  model: string
  /** Which operation this job runs: upscale / restore (Enhance) / sdr-to-hdr (HDR). */
  operation?: EnhanceOperation
  scale: number
  outputFormat?: string
  /** Full user-selected settings for retrying this job after failure. */
  retryOptions?: Record<string, unknown>
  error?: {
    code: string
    message: string
  }
  /** User who started the upscale, used to scope the initiator-only notification card. */
  userId?: string
  /** ISO timestamp of creation. */
  createdAt: string
  /** ISO timestamp of the last status write (also the LWW tiebreaker). */
  updatedAt: string
}

/** Coarse upscale status as persisted in Yjs (excludes the client-only `'pending'`). */
export type CoarseUpscaleStatus = Exclude<UpscaleStatus, 'pending'>

/** Monotonic ordering for coarse upscale statuses. Higher = further along; terminal states share the top. */
const UPSCALE_STATUS_RANK: Record<CoarseUpscaleStatus, number> = {
  queued: 1,
  processing: 2,
  uploading: 3,
  completed: 4,
  failed: 4,
}

/**
 * True when transitioning to `next` is a forward (or terminal) move from `current`.
 *
 * The backend re-broadcasts `queued`/`processing` on every poll and FAL can re-queue, so a
 * `processing -> queued` tick can legitimately arrive; treating status writes as monotonic prevents
 * those from walking the shared progress backward. Terminal states always win.
 */
export function isForwardUpscaleStatus(current: CoarseUpscaleStatus, next: CoarseUpscaleStatus): boolean {
  if (next === 'completed' || next === 'failed') return true
  return UPSCALE_STATUS_RANK[next] > UPSCALE_STATUS_RANK[current]
}

/** True for terminal statuses (a completed/failed job is no longer in flight). */
export function isTerminalUpscaleStatus(status: UpscaleStatus): boolean {
  return status === 'completed' || status === 'failed'
}

export function findActiveUpscaleForSourceIds(
  activeUpscales: Map<string, UpscaleJob>,
  assetIds: Iterable<string>,
): UpscaleJob | undefined {
  const sourceIds = new Set(assetIds)
  if (sourceIds.size === 0) return undefined

  for (const job of activeUpscales.values()) {
    if (sourceIds.has(job.sourceAssetId) && job.status !== 'completed' && job.status !== 'failed') {
      return job
    }
  }
  return undefined
}

export function listUnsettledUpscalesForSourceIds(
  activeUpscales: Map<string, UpscaleJob>,
  assetIds: Iterable<string>,
): UpscaleJob[] {
  const sourceIds = new Set(assetIds)
  if (sourceIds.size === 0) return []

  const jobs: UpscaleJob[] = []
  for (const job of activeUpscales.values()) {
    if (sourceIds.has(job.sourceAssetId) && job.status !== 'completed') {
      jobs.push(job)
    }
  }
  return jobs.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
}

interface UpscaleStore {
  /** Currently active upscale jobs, keyed by upscaleId */
  activeUpscales: Map<string, UpscaleJob>

  /** Add or update an upscale job */
  upsertUpscale: (job: UpscaleJob) => void

  /** Remove an upscale job from the store */
  removeUpscale: (upscaleId: string) => void

  /** Update upscale progress/status */
  updateUpscaleProgress: (
    upscaleId: string,
    updates: Partial<
      Pick<UpscaleJob, 'status' | 'progress' | 'message' | 'resultAssetId' | 'error' | 'completedAt' | 'refundedAmount'>
    >,
  ) => void

  /** Clear all completed/failed upscales */
  clearCompleted: () => void

  /** Check if any upscale is currently in progress */
  hasActiveUpscale: () => boolean

  /** Get an in-progress (non-terminal) upscale job for a specific source asset (if any) */
  getUpscaleForAsset: (assetId: string) => UpscaleJob | undefined

  /** Get an in-progress (non-terminal) upscale job for any source asset in a version group */
  getActiveUpscaleForSourceIds: (assetIds: Iterable<string>) => UpscaleJob | undefined

  /** Get all unsettled upscale jobs for any source asset in a version group */
  getUnsettledUpscalesForSourceIds: (assetIds: Iterable<string>) => UpscaleJob[]

  /** Get all upscales for a project */
  getUpscalesForProject: (projectId: string) => UpscaleJob[]

  /** Mark jobs with no recent server confirmation as failed. Returns number of cleared jobs. */
  clearStale: (maxAgeMs?: number) => number
}

// =============================================================================
// Store
// =============================================================================

export const useUpscaleStore = create<UpscaleStore>((set, get) => ({
  activeUpscales: new Map(),

  upsertUpscale: job => {
    set(state => {
      const newMap = new Map(state.activeUpscales)
      newMap.set(job.upscaleId, job)
      return { activeUpscales: newMap }
    })
  },

  removeUpscale: upscaleId => {
    set(state => {
      const newMap = new Map(state.activeUpscales)
      newMap.delete(upscaleId)
      return { activeUpscales: newMap }
    })
  },

  updateUpscaleProgress: (upscaleId, updates) => {
    set(state => {
      const existing = state.activeUpscales.get(upscaleId)
      if (!existing) return state

      const newMap = new Map(state.activeUpscales)
      // A real WebSocket progress/status update has arrived → mark the job live so the UI switches
      // from the indeterminate (Yjs-hydrated) treatment to a determinate bar.
      newMap.set(upscaleId, { ...existing, ...updates, hasLiveProgress: true, lastUpdateAt: new Date() })
      return { activeUpscales: newMap }
    })
  },

  clearCompleted: () => {
    set(state => {
      const newMap = new Map(state.activeUpscales)
      for (const [id, job] of newMap) {
        if (job.status === 'completed' || job.status === 'failed') {
          newMap.delete(id)
        }
      }
      return { activeUpscales: newMap }
    })
  },

  hasActiveUpscale: () => {
    const upscales = get().activeUpscales
    for (const job of upscales.values()) {
      if (job.status !== 'completed' && job.status !== 'failed') {
        return true
      }
    }
    return false
  },

  getUpscaleForAsset: assetId => {
    const upscales = get().activeUpscales
    for (const job of upscales.values()) {
      if (job.sourceAssetId === assetId && job.status !== 'completed' && job.status !== 'failed') {
        return job
      }
    }
    return undefined
  },

  getActiveUpscaleForSourceIds: assetIds => {
    return findActiveUpscaleForSourceIds(get().activeUpscales, assetIds)
  },

  getUnsettledUpscalesForSourceIds: assetIds => {
    return listUnsettledUpscalesForSourceIds(get().activeUpscales, assetIds)
  },

  getUpscalesForProject: projectId => {
    const upscales = get().activeUpscales
    const projectUpscales: UpscaleJob[] = []
    for (const job of upscales.values()) {
      if (job.projectId === projectId) {
        projectUpscales.push(job)
      }
    }
    return projectUpscales.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  },

  clearStale: (maxAgeMs = 70 * 60 * 1000) => {
    const now = Date.now()
    let cleared = 0
    set(state => {
      const newMap = new Map(state.activeUpscales)
      for (const [id, job] of newMap) {
        if (job.status !== 'completed' && job.status !== 'failed' && now - job.lastUpdateAt.getTime() > maxAgeMs) {
          newMap.set(id, {
            ...job,
            status: 'failed',
            error: { code: 'CLIENT_TIMEOUT', message: 'Upscale lost connection to the server — please refresh.' },
            completedAt: new Date(),
            lastUpdateAt: new Date(),
          })
          cleared++
        }
      }
      return cleared > 0 ? { activeUpscales: newMap } : state
    })
    return cleared
  },
}))

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Concise display label for the chosen HDR output spec (for notifications).
 * Returns undefined for plain upscales/enhances — there's no output choice to show.
 */
export function getUpscaleOutputLabel(hdrOutputSpec: string | undefined): string | undefined {
  switch (hdrOutputSpec) {
    case 'h265-main10':
      return 'H.265'
    case 'prores-pq':
      return 'ProRes'
    case 'exr-16bit-aces':
      return 'EXR'
    default:
      return undefined
  }
}

/**
 * Get a human-readable model name for display.
 */
export function getUpscaleModelDisplayName(model: string): string {
  // Video models
  const videoModels: Record<string, string> = {
    'prob-4': 'Proteus v4',
    'ghq-5': 'Gaia HQ v5',
    'iris-3': 'Iris v3',
    'nyx-3': 'Nyx v3',
    'thf-4': 'Theia Fine v4',
    'apo-8': 'Apollo v8',
    'sl-1': 'Starlight Precise 1',
    'slp-2': 'Starlight Precise 2',
    'slp-2.5': 'Starlight Precise 2.5',
    'slhq-1': 'Starlight HQ',
    'slm-1': 'Starlight Mini',
    'wonder-1': 'Starlight Sharp',
    'slf-1': 'Starlight Fast 1',
    'slf-2': 'Starlight Fast 2',
    'hyp-1': 'Hyperion 1',
    'hyp-2': 'Hyperion 2',
    'hyp-2.5': 'Hyperion 2.5',
  }

  // Image models (these come through as-is from the API)
  const imageModels: Record<string, string> = {
    'Standard V2': 'Standard V2',
    'Low Resolution V2': 'Low Res V2',
    'High Fidelity V2': 'High Fidelity',
    CGI: 'CGI',
    'Standard MAX': 'Standard MAX',
    'Recovery V2': 'Recovery V2',
    Redefine: 'Redefine',
  }

  return videoModels[model] || imageModels[model] || model
}

/**
 * Get estimated progress based on status for smoother UX.
 * Maps discrete statuses to progress ranges.
 */
export function getEstimatedProgress(status: UpscaleStatus, actualProgress: number): number {
  const progress = Number.isFinite(actualProgress) ? actualProgress : 0
  switch (status) {
    case 'pending':
      return Math.round(Math.min(progress, 2))
    case 'queued':
      return Math.round(Math.max(2, Math.min(progress, 5)))
    case 'processing':
      return Math.round(Math.max(5, Math.min(progress, 95)))
    case 'uploading':
      return Math.round(Math.max(95, Math.min(progress, 99)))
    case 'completed':
      return 100
    case 'failed':
      return Math.round(progress)
    default:
      return Math.round(progress)
  }
}
